import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { cancel, later, run, schedule, scheduleOnce } from '@ember/runloop';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import { tracked } from '@glimmer/tracking';

import type {
	BoardPreviewMap,
	PaginationDirection,
	PopupData,
	PopupListItem,
	RecordData,
	Service,
} from 'webapp-types';
import { ActivityLogEvent } from 'webapp/enums/activity-log';
import {
	BillingFeature,
	PopupContextSwitchAction as ContextSwitchAction,
	DataPerformanceSummaryType,
	PopupEvent,
	TemplateObjectType,
} from 'webapp/enums/enums';
import { TourId } from 'webapp/enums/tour';
import { TrackingEvent } from 'webapp/enums/tracking';
import { MD_BREAKPOINT } from 'webapp/utils/common';
import { getMaxSizeForRatio, isElementVisible } from 'webapp/utils/html';

import didInsert from 'webapp/modifiers/did-insert';
import BoardBoardOrLoopPreviewComponent from 'webapp/components/board/board-or-loop-preview';
import CommonCollapsiblePillOptionsComponent from 'webapp/components/common/collapsible-pill-options';
import CommonGenieInputComponent from 'webapp/components/common/genie/input';
import CommonRenderingPreviewHeaderComponent from 'webapp/components/common/rendering/preview-header';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import DataPerformanceSummaryButtonComponent from 'webapp/components/data-performance/summary-button';
import NotificationsButtonComponent from 'webapp/components/notifications/button';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import VerificationBadgeComponent from 'webapp/components/verification/badge';

import type {
	Action as BoardModelAction,
	TrackingOriginForDataboardDelete,
	TrackingOriginForDataboardDuplicate,
	TrackingOriginForFavoriteBoardAdded,
} from 'webapp/models-custom/board';
import type Board from 'webapp/models-custom/board';
import type BoardLoop from 'webapp/models-custom/board-loop';
import type { Action as BoardLoopModelAction } from 'webapp/models-custom/board-loop';
import type Template from 'webapp/models-custom/template';
import type FirstBoardEditTour from 'webapp/models-custom/tour/first-board-edit';
import type FirstBoardShareTour from 'webapp/models-custom/tour/first-board-share';

import type { TrackingOriginType as BoardLoopsTrackingOriginType } from 'webapp/services/model-manager/board-loops';
import type {
	TrackingOriginType as BoardsTrackingOriginType,
	TrackingOriginTypeForAddLoop as BoardsTrackingOriginTypeForAddLoop,
	TrackingOriginTypeForAddReport as BoardsTrackingOriginTypeForAddReport,
} from 'webapp/services/model-manager/boards';
import type { DataPerformanceSummaryObject } from 'webapp/services/model-manager/data-performance-summaries';

const GENIE_REVEAL_DELAY_MS = 2500;

const GENIE_PLACEHOLDERS = [
	'Let your AI analyst explain this',
	'Ask a question about this dashboard',
	'Ask anything about this dashboard',
	"What's driving these numbers?",
	'Ask Genie to explain this dashboard',
	'Curious about a metric? Ask away',
	'What do you want to know about this data?',
];

type Action =
	| 'downloadSnapshot'
	| 'openInternalUrl'
	| 'openShareLoop'
	| 'editLoop'
	| 'openWallShareSettings'
	| BoardModelAction
	| BoardLoopModelAction;

type Option = PopupListItem<Action>;

export default class BoardPopupPreviewComponent extends PopupInnerModalComponent<PopupData.Board.Preview.Any> {
	@service('ai-chat') declare aiChatService: Service.AIChat;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('account') declare accountService: Service.Account;
	@service('onboarding') declare onboardingService: Service.Onboarding;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('popup') declare popupService: Service.Popup;
	@service('request') declare requestService: Service.Request;
	@service('socket') declare socketService: Service.Socket;
	@service('tour') declare tourService: Service.Tour;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/board-loops')
	declare mmBoardLoopsService: Service.ModelManager.BoardLoops;
	@service('model-manager/activity-logs')
	declare mmActivityLogsService: Service.ModelManager.ActivityLogs;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('billing') declare billingService: Service.Billing;
	@service('store') declare storeService: Service.Store;
	@service('model-manager/templates') declare mmTemplatesService: Service.ModelManager.Templates;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;

	@tracked board: Board | null;
	@tracked template: Template | null;
	@tracked loop: BoardLoop | null;
	@tracked currentVisibleBoard?: Board;

	@tracked isWallView = false;
	@tracked previewMap: BoardPreviewMap = {};

	@tracked isDownloadingSnapshot = false;

	@tracked isGenieInputRevealed = false;
	private genieRevealTimer?: ReturnType<typeof later>;

	nodeActions: HTMLElement | null = null;
	@tracked bubbleTour1: FirstBoardEditTour | null = null;
	@tracked bubbleTour2: FirstBoardShareTour | null = null;

	// ------ Popup lifecycle ------
	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.args.model.on(PopupEvent.CLOSED, this, () => {
			this.mmBoardsService.checkMaxBoardIdsToRemember();
		});

		this.board = (this.popupData as PopupData.Board.Preview.ForBoard).board || null;
		this.template = (this.popupData as PopupData.Board.Preview.ForTemplate).template || null;
		this.loop = (this.popupData as PopupData.Board.Preview.ForLoop).loop || null;

		this.isWallView = window.innerWidth >= MD_BREAKPOINT;

		const template = this.template;

		if (template) {
			if (this.isBoardLoopTemplate) {
				template.boardIds.forEach((boardId) => {
					this.getPublicBoardData(String(boardId), template.token);
				});

				const boardLoop = this.mmBoardLoopsService.findModel(String(template.objectId)); // get the board loop model if exists

				if (boardLoop) {
					this.loop = boardLoop;
				} else {
					this.mmTemplatesService
						.requestPublicBoardLoopData(template.token)
						.then(
							(reply) => {
								this.storeService.pushPayload(reply);

								this.loop = this.mmBoardLoopsService.findModel(
									String(template.objectId),
								);
							},
							(errorData) => {
								throw errorData;
							},
						)
						.catch(
							this.requestService.showAllValidationErrors.bind(this.requestService),
						);
				}
			} else {
				const board = this.getPublicBoardData(String(template.objectId), template.token);

				if (!board) {
					this.close();
					return;
				}

				this.board = board;
			}
		}

		schedule('afterRender', () => {
			this.currentVisibleBoard = this.mainBoard;

			this.updatePopupSize();

			$(window).on('resize.modalBoard', () => {
				run(this, this.updatePopupSize);
			});

			later(() => {
				$('.popup-type-board-preview .scroll-box').fadeOut();
			}, 3000);

			this.scheduleRepositionSliderContent();
		});
	}

	get headingTitle() {
		return this.loop?.title || this.mainBoard?.name || '';
	}

	get isBoardLoop() {
		return !!this.loop;
	}

	get isBoardLoopTemplate() {
		return this.template?.objectType === TemplateObjectType.BOARD_LOOP;
	}

	get isTemplate() {
		return !!this.template;
	}

	get showBottomSlider() {
		return this.isBoardLoopTemplate || this.isBoardLoop;
	}

	get showDataPerformanceSummary() {
		return !this.template && !this.loop && !!this.board;
	}

	get dataPerformanceSummaryObject(): DataPerformanceSummaryObject | null {
		const boardId = this.board?.id;

		if (this.showDataPerformanceSummary && boardId) {
			if (this.previewMap[boardId]) {
				return {
					objectType: DataPerformanceSummaryType.BOARD_PREVIEW,
					objectId: this.previewMap[boardId],
				};
			} else {
				return {
					objectType: DataPerformanceSummaryType.BOARD,
					objectId: boardId,
				};
			}
		}

		return null;
	}

	get target() {
		return (this.loop || this.board)!;
	}

	get trackingOrigin() {
		return this.popupData.trackingOrigin;
	}

	get isSwitcherShown() {
		return !!this.popupData.isSwitcherShown;
	}

	get internalUrl(): string {
		const target = this.target;

		if (!target) {
			return '';
		}

		return target.getInternalUrlWithPreviewHash(this.previewMap);
	}

	get mainBoard() {
		// this will be used in the permissions logic etc. for loops = first board
		return this.board || this.loop?.boards[0];
	}

	get showGenieInput() {
		if (
			this.aiChatService.isOpen ||
			this.navigationService.activatedRoute.startsWith('genie')
		) {
			return false;
		}

		return this.aiChatService.isEligibleForAiChat && this.board && !this.isTemplate;
	}

	get isGenieInputVisible() {
		return this.showGenieInput && this.isGenieInputRevealed;
	}

	get geniePlaceholders(): string[] {
		return GENIE_PLACEHOLDERS;
	}

	@action
	changeCurrentVisibleBoard(boardId: string) {
		this.currentVisibleBoard =
			this.board || this.loop?.boards.find((item) => item.id === boardId);
	}

	@action
	onBoardsDataLoaded() {
		const onBoardsDataLoadedFn = (this.popupData as PopupData.Board.Preview.ForBoard)
			.onBoardsDataLoaded;
		if (onBoardsDataLoadedFn) {
			onBoardsDataLoadedFn();
		}

		this.scheduleGenieReveal();
	}

	private scheduleGenieReveal(): void {
		if (this.isGenieInputRevealed || this.genieRevealTimer) {
			return;
		}

		this.genieRevealTimer = later(
			this,
			() => {
				this.genieRevealTimer = undefined;
				this.isGenieInputRevealed = true;
			},
			GENIE_REVEAL_DELAY_MS,
		);
	}

	private getPublicBoardData(boardId: string, templateToken: string) {
		let board;

		if (this.mmBoardsService.isBoardDataRequested(boardId)) {
			board = this.mmBoardsService.findModel(boardId);
		} else {
			// for acc template, we might already have the board model
			board = this.mmBoardsService.findModel(boardId);
			if (!board) {
				// create a "fake" temporary one, which will be updated when the request finishes
				this.storeService.pushPayload({ board: { id: boardId, color: '' } });
				board = this.mmBoardsService.findModel(boardId);
			}

			// requesting on the "public" URL since this board might not be in the current space
			let url = `/token/${templateToken}/board/${boardId}`; // TODO: first board id in board loop - handle empty board loop without boards

			this.requestService
				.api<RecordData.Payload>('GET', url)
				.then(
					(reply) => {
						delete reply.template; // so we don't rerender opened connect popup
						this.storeService.pushPayload(reply);
						this.mmBoardsService.markBoardRequested(boardId);

						// this request triggers samples that come over pusher dataUpdated / bgEvent
						// for templates, we still subscribe to space-user-board channel
						void this.socketService.afterDataChannelSubscribed
							?.then(() => {
								return this.socketService.subscribeToBoardData(+boardId);
							})
							.then(() => {
								url += '/data';
								this.requestService
									.api<RecordData.Payload>('GET', url)
									.then((samplesReply) => {
										this.storeService.pushPayload(samplesReply);
									})
									.catch(
										this.requestService.handleGenericThrownError.bind(
											this.requestService,
										),
									);
							})
							.catch();
					},
					(errorData) => {
						this.close();
						throw errorData;
					},
				)
				.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
		}

		return board;
	}

	// ------ helpers for popup resizes ------

	updatePopupSize(): void {
		if (!this.isWallView) {
			return;
		}

		const wallSize = getMaxSizeForRatio($(window), 1.638);

		const topPos = (window.innerHeight - wallSize.height) / 2;

		const size: Record<string, number> = {
			width: wallSize.width,
			height: wallSize.height,
			top: topPos,
			bottom: topPos,
			left: (window.innerWidth - wallSize.width) / 2,
		};

		const popupStyle = htmlSafe(
			Object.keys(size)
				.map((key) => {
					return key + ':' + String(size[key]) + 'px';
				})
				.join(';'),
		);

		const popupCM = this.args.model;
		if (popupCM) {
			schedule('afterRender', () => {
				popupCM.popupStyle = popupStyle;
			});
		}

		this.scheduleRepositionSliderContent();
	}

	scheduleRepositionSliderContent(): void {
		scheduleOnce('afterRender', this, this.repositionSliderContent);
	}

	repositionSliderContent(): void {
		try {
			// fix slide dimensions
			$('#slider-ct').slick('getSlick')?.setPosition();
		} catch (_e) {
			// not important
		}

		$(window).trigger('resize.datawallcenter');
		if (this.bubbleTour1) {
			this.bubbleTour1.repositionBubble();
		}
		if (this.bubbleTour2) {
			this.bubbleTour2.repositionBubble();
		}
	}

	// ------ Tour handling ------

	checkBubbleTour(): void {
		if (this.template || this.loop) {
			return;
		}

		// setup bubble tours
		if (this.mainBoard?.canCurrentUserEdit) {
			// share tour opens AFTER edit bubble (if user dismisses) OR when user opens popup 2nd time
			const hasTour1 = this.tourService.isEligibleForFirstBoardEditTour,
				hasTour2 = this.tourService.isEligibleForFirstBoardShareTour,
				nodeEditButton = this.nodeActions?.querySelector<HTMLElement>('.edit');

			const startTour2 = () => {
				const nodeShareButton = this.nodeActions?.querySelector<HTMLElement>('.share');

				later(() => {
					if (
						this.mainBoard &&
						!this.bubbleTour2 &&
						nodeShareButton &&
						isElementVisible(nodeShareButton)
					) {
						const tour = this.tourService.initTour(TourId.FIRST_BOARD_SHARE, {
							el: $(nodeShareButton).eq(0),
						});
						if (tour) {
							tour.start();
							this.bubbleTour2 = tour;
						}
					}
				}, 2000);
			};

			if (hasTour1 && nodeEditButton && isElementVisible(nodeEditButton)) {
				const timeout = !this.currentUserService.isOnboarded ? 10 : 2;

				schedule('afterRender', this, () => {
					later(() => {
						if (!this.bubbleTour1) {
							const tour = this.tourService.initTour(TourId.FIRST_BOARD_EDIT, {
								el: $(nodeEditButton).eq(0),
							});
							if (tour) {
								if (hasTour2) {
									tour.onExplicitClose = function () {
										tour.end();
										startTour2();
									};
								}
								tour.start();
								this.bubbleTour1 = tour;
							}
						}
					}, timeout * 1000);
				});
			} else if (hasTour2) {
				scheduleOnce('afterRender', this, startTour2);
			}
		}
	}

	// ---- Actions -----

	get options(): Option[] {
		const options: Option[] = [];

		const fullscreenOption: Option = {
			icon: 'FullScreen',
			text: 'Full-screen view in new tab',
			action: 'openInternalUrl',
		};

		options.push({
			icon: 'Download',
			text: 'Download snapshot',
			action: 'downloadSnapshot',
			isLoading: this.isDownloadingSnapshot,
		});

		if (this.loop) {
			if (this.loop.canCurrentUserEdit) {
				options.push({
					icon: 'Share',
					text: 'Share',
					action: 'openShareLoop',
				});
			}

			options.push(fullscreenOption);

			if (this.loop.canCurrentUserEditSomeBoard) {
				options.push({
					icon: 'Pencil',
					text: 'Edit loop',
					action: 'editLoop',
				});
			}
			options.push(...this.loop.getAdditionalOptions(false, true));
		} else if (this.mainBoard) {
			options.push({
				icon: 'Share',
				text: 'Share',
				action: 'openWallShareSettings',
			});

			options.push(fullscreenOption);

			options.push(...this.mainBoard.getAdditionalOptions(false, true));

			if (!this.isWallView) {
				options.unshift({
					icon: 'Share',
					text: 'Share',
					action: 'openWallShareSettings',
				});
			}
		}

		return options;
	}

	@action
	doAction(actionName?: string): void {
		if (!actionName) {
			return;
		}

		void this[actionName as Action]();
	}

	@action
	useTemplate(): boolean {
		const onTemplateUse = (this.popupData as PopupData.Board.Preview.ForTemplate).onTemplateUse;
		if (this.template && onTemplateUse) {
			onTemplateUse(this.template);
			return true;
		}
		return false;
	}

	@action
	verify(): void {
		const board = this.mainBoard;
		if (board) {
			void this.mmBoardsService.toggleVerification(board);
		}
	}

	@action
	openInternalUrl(): void {
		if (this.accountService.handleDemo()) {
			return;
		}

		window.open(this.internalUrl, '_blank');
	}

	@action
	openOptions(): void {
		// for mobile view
		const attachTo = $('.popup-type-board-preview .actions .context').get(0);
		const popup = this.popupService.createListPopup(attachTo);

		if (popup) {
			popup.open({
				itemsList: this.options,
				doAction: (actionName: string) => {
					const actionFn = this[actionName as keyof this];
					if (typeof actionFn === 'function') {
						(actionFn as () => void)();
					}
				},
			});
		}
	}

	@action
	edit() {
		if (this.isBoardLoop) {
			this.openLoopInDesigner();
		} else {
			this.editBoard();
		}
	}

	@action
	editBoard(): void {
		if (this.accountService.handleDemo()) {
			return;
		}

		if (!this.mainBoard) {
			return;
		}

		this.analyticsService.event(TrackingEvent.DATABOARD_EDIT, {
			databoard_id: this.mainBoard.id,
			origin: this.trackingOrigin,
		});
		this.onboardingService.triggerEventOnFirstEditViaBoardPreview();
		this.mainBoard?.editBoard();
	}

	@action
	openWallShareSettings(): void {
		const board = this.mainBoard;

		if (!board?.id) {
			return;
		}

		board.openWallShareSettings(
			this.trackingOrigin as BoardsTrackingOriginType,
			this.previewMap[board.id],
		);
	}

	@action
	addToReport(): void {
		if (!this.mainBoard) {
			return;
		}

		this.mmBoardsService.addToReport(
			this.mainBoard,
			this.trackingOrigin as BoardsTrackingOriginTypeForAddReport,
		);
	}

	@action
	addToLoop(): void {
		if (!this.mainBoard) {
			return;
		}

		this.mmBoardsService
			.addToLoop(this.mainBoard, this.trackingOrigin as BoardsTrackingOriginTypeForAddLoop)
			.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
	}

	@action
	openFullscreenView(): void {
		const loop = this.loop,
			board = this.board;

		if (loop) {
			this.mmBoardLoopsService.openFullscreenView(loop);
		} else if (board) {
			board.openFullscreenView();
		}
	}

	@action
	openPublicTemplateEditPopup(): void {
		if (this.mainBoard) {
			this.mmBoardsService.openPublicTemplateEditPopup(this.mainBoard);
		} else if (this.loop) {
			this.mmBoardLoopsService.openAccountTemplateEditPopup(this.loop, 'board-loops');
		}
	}

	@action
	openAccountTemplateEditPopup(): void {
		if (!this.mainBoard) {
			return;
		}

		this.mmBoardsService.openAccountTemplateEditPopup(this.mainBoard, 'board_preview');
	}

	@action
	toggleFavorite(): void {
		if (this.accountService.handleDemo()) {
			return;
		}
		if (this.loop) {
			this.mmBoardLoopsService.toggleFavorite(this.loop);
		} else {
			this.mainBoard?.toggleFavorite(
				this.trackingOrigin as TrackingOriginForFavoriteBoardAdded,
			);
		}
	}

	@action
	copyBoard(): void {
		if (this.accountService.handleDemo()) {
			return;
		}

		if (this.mainBoard) {
			this.mainBoard?.copyBoard(this.trackingOrigin as TrackingOriginForDataboardDuplicate);
		}
	}

	@action
	duplicate(): void {
		if (this.accountService.handleDemo()) {
			return;
		}
		if (this.loop) {
			this.duplicateLoop();
		}
	}

	@action
	transferBoard(): void {
		const board = this.mainBoard;
		if (board) {
			this.mmBoardsService
				.openTransferPopup(board)
				.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
		}
	}

	@action
	transferBoardToAgency() {
		const board = this.mainBoard;
		if (board) {
			this.mmBoardsService
				.openTransferPopup(board, false, true)
				.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
		}
	}

	@action
	deleteBoard(): void {
		if (this.accountService.handleDemo()) {
			return;
		}

		this.mainBoard?.deleteObject(
			this.trackingOrigin as TrackingOriginForDataboardDelete,
			() => {
				this.close();
				return 0;
			},
		);
	}

	@action
	openMigrationTool() {
		if (!this.mainBoard) {
			return;
		}
		this.mmBoardsService.openMigrationTool(this.mainBoard);
	}

	@action
	toggleMobileVisibility(): void {
		if (this.accountService.handleDemo()) {
			return;
		}

		const model = this.mainBoard;

		if (!model) {
			return;
		}

		const layer = this.infolayerService.loading('Saving...');

		model.hidden = !model.hidden;
		model
			.save()
			.then(
				(boardRecord) => {
					layer.ok(
						'Dashboard <strong>' +
							boardRecord.name +
							'</strong> ' +
							(boardRecord.hidden ? 'hidden' : 'visible') +
							' on mobile!',
					);
				},
				(errorData) => {
					layer.close();
					throw errorData;
				},
			)
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	@action
	openActivityLog() {
		if (this.board) {
			this.mmActivityLogsService.createAndOpenPopup({
				model: this.board,
			});
		} else if (this.loop) {
			this.mmActivityLogsService.createAndOpenPopup({
				model: this.loop,
			});
		}
	}

	private editLoop(): void {
		if (this.accountService.handleDemo()) {
			return;
		}

		const loop = this.loop;
		if (loop) {
			this.mmBoardLoopsService.openEditPopup(loop);
		}
	}

	@action
	openShareLoop(): void {
		if (this.accountService.handleDemo()) {
			return;
		}

		const loop = this.loop;
		if (loop) {
			this.mmBoardLoopsService.openSharePopup(
				loop,
				this.trackingOrigin as BoardLoopsTrackingOriginType,
				this.previewMap,
			);
		}
	}

	@action
	openLoopInDesigner(): void {
		if (this.accountService.handleDemo()) {
			return;
		}

		const loop = this.loop;
		if (loop) {
			this.mmBoardLoopsService.openInDesigner(loop);
		}
	}

	@action
	duplicateLoop(): void {
		const loop = this.loop;
		if (loop) {
			this.mmBoardLoopsService.duplicate(loop).then(
				(copiedLoopRecord) => {
					const copiedLoopModel = this.mmBoardLoopsService.createModel(copiedLoopRecord);

					this.navigationService.createContextSwitchModal().open({
						actionType: ContextSwitchAction.DUPLICATE,
						sourceItem: loop,
						targetItem: copiedLoopModel,
						onSwitch: () => {
							this.loop = copiedLoopModel;
							if (this.loop.id) {
								this.mmActivityLogsService.createAndSaveActivityLogItem(
									ActivityLogEvent.BOARD_LOOP_VIEW,
									this.loop.id,
								);
							}
						},
					});
				},
				() => {
					// skip
				},
			);
		}
	}

	@action
	deleteLoop(): void {
		const loop = this.loop;
		if (loop) {
			this.mmBoardLoopsService.delete(loop, () => {
				this.close();
				return 0;
			});
		}
	}

	@action
	switchBoard(direction: PaginationDirection): {
		board: Board | null;
		loop: BoardLoop | null;
	} {
		const onSwitchBoard = this.popupData.onSwitchBoard;

		if (!onSwitchBoard) {
			return { board: this.board, loop: this.loop };
		}

		this.updatePreviewMap({});

		const board = this.board,
			loop = this.loop;
		let objectId,
			eventType = ActivityLogEvent.BOARD_VIEW;

		if (board) {
			this.board = onSwitchBoard(this.board!, direction) as Board;
			objectId = this.board.id;
		} else if (loop) {
			this.loop = onSwitchBoard(this.loop!, direction) as BoardLoop;
			objectId = this.loop.id;
			eventType = ActivityLogEvent.BOARD_LOOP_VIEW;

			const board = this.loop.boards[0];
			if (board) {
				this.currentVisibleBoard = board;
			}
		}

		if (objectId) {
			this.mmActivityLogsService.createAndSaveActivityLogItem(eventType, objectId);
		}

		return { board, loop };
	}

	@action
	updatePreviewMap(previewMap: BoardPreviewMap) {
		this.previewMap = previewMap;
	}

	@action
	createReport(): void {
		if (!this.loop) {
			return;
		}
		this.mmReportsService.createFromLoop(this.loop, 'loops');
	}

	@action
	saveAsTemplate(): void {
		if (!this.loop) {
			return;
		}
		this.mmBoardLoopsService.openAccountTemplateEditPopup(this.loop, 'board-loops');
	}

	@action
	handleGenieInput(prompt: string): void {
		if (!this.showGenieInput || !this.board || !prompt.length) {
			return;
		}

		this.aiChatService.openGenieForBoard(this.board, prompt, { keepPopups: true });
	}

	async downloadSnapshot() {
		if (this.isDownloadingSnapshot) {
			return;
		}

		if (!this.billingService.checkIfCan(BillingFeature.SHARING, true)) {
			return;
		}

		this.isDownloadingSnapshot = true;
		if (this.isBoardLoop) {
			await this.mmBoardLoopsService.downloadSnapshotWithInfolayer(this.loop!);
		} else {
			await this.mmBoardsService.downloadSnapshotWithInfolayer(this.mainBoard!);
		}
		this.isDownloadingSnapshot = false;
	}

	@action
	setupView(node: HTMLElement) {
		this.nodeActions = node.previousElementSibling?.querySelector('.actions') || null; // .title . actions

		this.checkBubbleTour();
	}

	override willDestroy() {
		$(window).off('resize.modalBoard');

		if (this.genieRevealTimer) {
			cancel(this.genieRevealTimer);
		}

		let tour = this.bubbleTour1;
		if (tour) {
			tour.end();
		}

		tour = this.bubbleTour2;
		if (tour) {
			tour.end();
		}

		if (this.board) {
			this.onboardingService.triggerActionOnBoardPreviewClose();
		}

		this.popupService.closeOnlyContextPopups();
	}

	// These actions are part of the model Action types but are handled elsewhere, not in the preview popup
	createNotification(): void {
		// unused
	}
	addToTeam(): void {
		// unused
	}
	openChangeOwnerPopup(): void {
		// unused
	}
	openPreview(): void {
		// unused
	}
	openInDesigner(): void {
		// unused
	}
	share(): void {
		// unused
	}
	restore(): void {
		// unused
	}
	deletePermanently(): void {
		// unused
	}
	<template>
		{{#if this.isWallView}}

			<div class="title bgTheme-{{this.currentVisibleBoard.bgTheme}}">

				{{#if this.template}}

					<CommonRenderingPreviewHeaderComponent
						@model={{this.template}}
						@isEditBtnShown={{this.mainBoard.canCurrentUserEdit}}
						@isShownInPopup={{true}}
						@onUse={{this.useTemplate}}
						@onEdit={{this.editBoard}}
					/>

				{{else}}

					<div class="boardHeader">

						<h1>{{this.headingTitle}}</h1>
						<VerificationBadgeComponent
							@verification={{this.mainBoard.verificationInfo}}
							@entityLabel="databoard"
							@size="md"
						/>

						{{#if this.dataPerformanceSummaryObject}}
							<DataPerformanceSummaryButtonComponent
								@object={{this.dataPerformanceSummaryObject}}
								@trackingOrigin="preview_databoard"
								@iconSize="lg"
							/>
						{{/if}}

						<div class="actions-right">

							<div class="dbx-ui-actions">

								<CommonCollapsiblePillOptionsComponent
									@itemsList={{this.options}}
									@doAction={{this.doAction}}
								/>

								{{#unless this.currentUserService.isViewer}}
									<NotificationsButtonComponent
										@model={{this.target}}
										@origin="databoard_preview"
										@popupTheme={{this.currentVisibleBoard.bgTheme}}
										@buttonTheme="empty"
									/>
								{{/unless}}

								{{#if this.target.canCurrentUserEdit}}
									<a class="button" {{on "click" this.edit}}>
										<CommonUiIconComponent @name="Pencil" @size="sm" />Edit
									</a>
								{{/if}}

							</div>

						</div>
					</div>

				{{/if}}

			</div>

		{{else}}

			<div class="scroll-box"><span>Scroll</span></div>

			<div class="actions">
				<a role="button" class="context" {{on "click" this.openOptions}}>
					<CommonUiIconComponent @name="Ellipsis" @size="md" />
				</a>
			</div>

		{{/if}}

		<div
			class="content"
			style={{this.currentVisibleBoard.colorGradient}}
			{{didInsert this.setupView}}
		>
			<div id="datawall" class="border">

				{{#if this.isSwitcherShown}}
					<div class="switcher">
						<a
							role="button"
							class="switcher__prev"
							{{on "click" (fn this.switchBoard "prev")}}
						>
							<CommonUiIconComponent @name="ChevronDown" @size="md" />
						</a>
						<a
							role="button"
							class="switcher__next"
							{{on "click" (fn this.switchBoard "next")}}
						>
							<CommonUiIconComponent @name="ChevronDown" @size="md" />
						</a>
					</div>
				{{/if}}

				<BoardBoardOrLoopPreviewComponent
					@board={{this.board}}
					@loop={{this.loop}}
					@previewMap={{this.previewMap}}
					@updatePreviewMap={{this.updatePreviewMap}}
					@isInPopup={{true}}
					@isTemplate={{this.isTemplate}}
					@isBoardLoopTemplate={{this.isBoardLoopTemplate}}
					@showBottomSlider={{this.showBottomSlider}}
					@onBoardsDataLoaded={{this.onBoardsDataLoaded}}
					@onVisibleBoardChange={{this.changeCurrentVisibleBoard}}
				/>

			</div>
		</div>

		{{#if this.isGenieInputVisible}}
			<CommonGenieInputComponent
				@placeholder="Analyze with Genie..."
				@rotatingPlaceholders={{this.geniePlaceholders}}
				@theme="light"
				@onSubmit={{this.handleGenieInput}}
			/>
		{{/if}}
	</template>
}
